
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Typography, Grid, Card, CardContent, CardActions,
  Button, TextField, Box, FormControl, InputLabel, Select, MenuItem,
  Chip, InputAdornment, Paper, Skeleton, IconButton, Tooltip
} from '@mui/material';
import {
  Search, FilterList, LocationOn, Sell, ArrowForward, Inventory2,
  FavoriteBorder, ShoppingCart, SortRounded
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { inventoryAPI, wishlistAPI, cartAPI } from '../services/api';

const MotionCard = motion(Card);

const categories = [
  'Bearings', 'Motors', 'Pumps', 'Valves', 'Couplings',
  'Electrical', 'Hydraulic', 'Pneumatic', 'Tools', 'Raw Materials', 'Other'
];

const MarketplacePage = () => {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '', category: '', min_price: '', max_price: '', location: '', sort_by: 'newest',
  });

  useEffect(() => { loadItems(); }, []);

  const loadItems = async (params = {}) => {
    setLoading(true);
    try {
      const cleanParams = Object.fromEntries(
        Object.entries({ ...filters, ...params }).filter(([_, v]) => v !== '')
      );
      const response = await inventoryAPI.list(cleanParams);
      setItems(response.data);
    } catch (err) {
      console.error('Failed to load items', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => loadItems(filters);
  const handleReset = () => {
    const reset = { search: '', category: '', min_price: '', max_price: '', location: '', sort_by: 'newest' };
    setFilters(reset);
    loadItems(reset);
  };

  const handleAddToCart = async (itemId, price) => {
    if (!isAuthenticated) { toast.error('Login first'); return; }
    try {
      await cartAPI.add({ item_id: itemId, quantity: 1 });
      toast.success('Added to cart');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleWishlist = async (itemId) => {
    if (!isAuthenticated) { toast.error('Login first'); return; }
    try {
      await wishlistAPI.add(itemId);
      toast.success('Added to wishlist');
    } catch (err) { toast.error(err.response?.data?.detail || 'Already in wishlist'); }
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '80vh' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>Marketplace</Typography>
          <Typography variant="body1" color="text.secondary">
            Browse surplus industrial inventory uploaded by verified sellers
          </Typography>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }} elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterList color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>Search & Filter</Typography>
          </Box>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" label="Search items" value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                placeholder="e.g., SKF bearing, hydraulic pump..."
                onKeyPress={(e) => e.key === 'Enter' && handleFilter()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select value={filters.category} label="Category"
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
                  <MenuItem value="">All</MenuItem>
                  {categories.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth size="small" label="Min ₹" type="number"
                value={filters.min_price}
                onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth size="small" label="Max ₹" type="number"
                value={filters.max_price}
                onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Sort</InputLabel>
                  <Select value={filters.sort_by} label="Sort"
                    onChange={(e) => { setFilters({ ...filters, sort_by: e.target.value }); }}>
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="price_asc">Price ↑</MenuItem>
                    <MenuItem value="price_desc">Price ↓</MenuItem>
                    <MenuItem value="popular">Popular</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="contained" onClick={handleFilter} startIcon={<Search />}>Go</Button>
                <Button variant="outlined" onClick={handleReset} size="small">Reset</Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Results */}
        {!loading && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {items.length} items found
            </Typography>
          </Box>
        )}

        {/* Item Grid */}
        <Grid container spacing={3}>
          {loading ? (
            [...Array(6)].map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Skeleton variant="text" width="60%" height={32} />
                    <Skeleton variant="text" width="30%" />
                    <Skeleton variant="text" width="100%" />
                    <Skeleton variant="text" width="40%" height={40} />
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            items.map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <MotionCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3 }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                        {item.item_name}
                      </Typography>
                      <Box>
                        <Tooltip title="Add to Wishlist">
                          <IconButton size="small" onClick={() => handleWishlist(item.id)}>
                            <FavoriteBorder fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                      <Chip label={item.category} size="small" color="primary" variant="outlined" />
                      <Chip label={item.condition?.replace('_', ' ')} size="small"
                        color={item.condition === 'new' ? 'success' : 'default'} variant="outlined" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                      {item.description?.substring(0, 80) || 'Industrial surplus inventory item'}
                      {item.description?.length > 80 ? '...' : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                      <Typography variant="h5" color="primary.main" fontWeight={700}>
                        ₹{item.selling_price?.toLocaleString()}
                      </Typography>
                      {item.original_price && (
                        <Typography variant="body2" color="text.disabled" sx={{ textDecoration: 'line-through' }}>
                          ₹{item.original_price?.toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Sell sx={{ fontSize: 14 }} /> Qty: {item.quantity}
                      </Typography>
                      {item.location && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <LocationOn sx={{ fontSize: 14 }} /> {item.location}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                      variant="contained" size="small" startIcon={<ShoppingCart />}
                      onClick={() => handleAddToCart(item.id, item.selling_price)}
                      sx={{ flex: 1 }}
                    >
                      Add to Cart
                    </Button>
                    <Button
                      variant="outlined" size="small"
                      component={Link} to={`/item/${item.id}`}
                      endIcon={<ArrowForward />}
                    >
                      Details
                    </Button>
                  </CardActions>
                </MotionCard>
              </Grid>
            ))
          )}
          {!loading && items.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                <Inventory2 sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No items found
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Try adjusting your filters or check back later for new listings.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default MarketplacePage;



